import type { HeroContent } from "../../types/page-content";

interface Props {
  content: HeroContent;
  onEstimateClick: () => void;
}

export default function HeroSection({ content, onEstimateClick }: Props) {
  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105 transition-transform duration-[20s]"
        style={{ backgroundImage: `url('${content.bgImage}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-14 pt-32 pb-24">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-0.5 bg-safety-500" />
            <span className="font-body text-safety-500 text-xs uppercase tracking-widest2 font-700">
              {content.eyebrow}
            </span>
          </div>

          {/* H1 */}
          <h1 className="font-heading text-white leading-none tracking-display mb-7">
            <span className="block text-7xl lg:text-9xl">{content.headlineLine1}</span>
            <span className="block text-7xl lg:text-9xl text-safety-500">{content.headlineAccent}</span>
            <span className="block text-7xl lg:text-9xl">{content.headlineLine2}</span>
          </h1>

          <p className="font-body text-asphalt-200 text-lg lg:text-xl leading-relaxed max-w-xl mb-12">
            {content.subheadline}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={onEstimateClick}
              className="group flex items-center justify-center gap-3 bg-safety-500 text-asphalt-950 font-body font-800 text-sm uppercase tracking-widest px-10 py-5 rounded-none hover:bg-safety-400 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line text-lg" />
              Get Your Free Estimate
              <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="tel:5558007283"
              className="flex items-center justify-center gap-3 border border-white/40 text-white font-body font-700 text-sm uppercase tracking-widest px-10 py-5 rounded-none hover:border-safety-500 hover:text-safety-500 transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              <i className="ri-phone-fill text-base" />
              (555) 800-7283
            </a>
          </div>
        </div>

        {/* Stats */}
        <div className="absolute right-10 bottom-24 hidden xl:flex flex-col gap-0 border border-asphalt-600 bg-black/50 backdrop-blur-md">
          {[
            { num: "25+", label: "Years in Business" },
            { num: "1,200+", label: "Projects Completed" },
            { num: "100%", label: "Licensed & Insured" },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className={`px-8 py-5 ${i < 2 ? "border-b border-asphalt-600" : ""}`}
            >
              <div className="font-heading text-safety-500 text-4xl tracking-display">{stat.num}</div>
              <div className="font-body text-asphalt-300 text-xs uppercase tracking-widest mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section number */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden xl:block">
        <span className="font-heading text-white/5 text-[180px] leading-none select-none">01</span>
      </div>

      {/* Scroll */}
      <div className="absolute bottom-8 left-14 flex items-center gap-3">
        <div className="w-px h-12 bg-safety-500/60" />
        <span className="font-body text-asphalt-400 text-xs uppercase tracking-widest">Scroll</span>
      </div>
    </section>
  );
}
